import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '../config/keys';
import { useAuth } from '../context/AuthContext';
import { usePlanValidation } from '../hooks/usePlanValidation';
import { useFormPersistence } from '../hooks/useFormPersistence';
import PlanLimitMessage from './PlanLimitMessage';

import { 
  Building2, Key, 
  Loader, LogIn,
  ArrowRight, Check, X
} from 'lucide-react';

// Importar componentes de pasos para propiedades
import InfPublish, { capitalizeFirstLetter } from './publishes/InfPublish';
import UbiPublish from './publishes/UbiPublish';
import CarPublish from './publishes/CarPublish';
import LugPublish from './publishes/LugPublish';
import ImgPublish from './publishes/ImgPublish';

// Importar componentes de pasos para proyectos
import InfProyPublish from './publishes/InfProyPublish';
import CarProyPublish from './publishes/CarProyPublish';

// Constantes
const TIPO_CAMBIO = 3.65;
const MAX_IMAGES = 10;
const MIN_IMAGES = 1;
const LIBRARIES = ['places'];

const Publish = () => {
  const { operationType, publicationType } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { canPublish, loading: validatingPlan, error: planError, planInfo } = usePlanValidation();
  
  const searchBoxRef = useRef(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Estados
  const [step, setStep] = useState(1);
  const [showRecoveredBanner, setShowRecoveredBanner] = useState(false);
  
  // Estado inicial del formulario
  const initialFormData = {
    operacion: operationType || 'comprar',
    tipo: '',
    titulo: '',
    descripcion_corta: '',
    precio_soles: '',
    precio_dolares: '',
    precio_mensual: '',
    mantenimiento: '',
    ubicacion: '',
    mapa_direccion: '',
    latitude: null,
    longitude: null,
    area_ocupada: '',
    area_total: '',
    largo_terreno: '',
    ancho_terreno: '',
    habitaciones: '',
    banos: '',
    estacionamiento: false,
    antiguedad: '',
    areas_verdes: 'No',
    lugares_cercanos: [],
    servicios_basicos: [],
    imagenes: [],
    area_completa: false,
    // Campos específicos para proyectos
    nombre_proyecto: '',
    descripcion_proyecto: '',
    tipo_proyecto: '',
    estado_proyecto: '',
    mes_entrega: '',
    anio_entrega: '',
    total_unidades: '',
    tiene_1dorm: false,
    tiene_2dorm: false,
    tiene_3dorm: false,
    tiene_4dorm: false,
    unidades_1dorm: '',
    unidades_2dorm: '',
    unidades_3dorm: '',
    unidades_4dorm: '',
    area_1dorm: '',
    area_2dorm: '',
    area_3dorm: '',
    area_4dorm: '',
    precio_1dorm_soles: '',
    precio_2dorm_soles: '',
    precio_3dorm_soles: '',
    precio_4dorm_soles: '',
    precio_1dorm_dolares: '',
    precio_2dorm_dolares: '',
    precio_3dorm_dolares: '',
    precio_4dorm_dolares: '',
    tiene_financiamiento: 'No',
    bancos_financiamiento: [],
    otro_banco: '',
    detalles_financiamiento: '',
    amenidades: [],
    otras_caracteristicas: '',
  };

  // Usar hook de persistencia para el formulario
  const storageKey = `form_data_${operationType || 'comprar'}_${publicationType || 'propiedad'}`;
  const [formData, setFormData, clearFormData] = useFormPersistence(storageKey, initialFormData);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAutoConverting, setIsAutoConverting] = useState(false);
  
  // Google Maps Config
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  // Comprobar autenticación y verificar si hay datos guardados
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      setIsAuthenticated(!!storedToken);
      
      if (storedToken) {
        if (user?.tipo_usuario_id === 1) {
          navigate('/upgrade-plan');
        } else if (!canPublish && !validatingPlan) {
          if (planInfo?.totalProperties === 0) {
            navigate('/upgrade-plan');
          } else {
            navigate('/mis_propiedades');
          }
        }
      }
      
      // Verificar si hay datos guardados para mostrar banner
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          
          // Verificar si hay datos significativos guardados
          const hasSignificantData = publicationType === 'proyecto' 
            ? (parsedData.nombre_proyecto || parsedData.descripcion_proyecto || parsedData.tipo_proyecto)
            : (parsedData.titulo || parsedData.descripcion_corta || parsedData.mapa_direccion);
            
          if (hasSignificantData) {
            setShowRecoveredBanner(true);
            
            // Ajustar el paso según los datos recuperados
            if (parsedData.imagenes?.length > 0) {
              setStep(5);
            } else if (parsedData.lugares_cercanos?.length > 0) {
              setStep(4);
            } else if (parsedData.mapa_direccion && parsedData.latitude) {
              if (publicationType === 'proyecto') {
                setStep(3); // Para proyectos: info -> ubicación -> características
              } else {
                setStep(3); // Para propiedades: info -> ubicación -> características
              }
            } else if (publicationType === 'proyecto' 
              ? (parsedData.nombre_proyecto && parsedData.descripcion_proyecto)
              : (parsedData.titulo && parsedData.descripcion_corta)
            ) {
              setStep(2);
            }
          }
        } catch (error) {
          console.error('Error al analizar datos guardados:', error);
        }
      }
      
      setCheckingAuth(false);
    };

    checkAuth();
  }, [user, navigate, canPublish, validatingPlan, planInfo, storageKey, publicationType]);

  // Manejador de reordenamiento de imágenes
  useEffect(() => {
    const handleImagesReordered = (event) => {
      setFormData(prev => ({
        ...prev,
        imagenes: event.detail.images
      }));
    };

    document.addEventListener('imagesReordered', handleImagesReordered);
    return () => {
      document.removeEventListener('imagesReordered', handleImagesReordered);
    };
  }, []);

  // Mostrar loader mientras verifica
  if (checkingAuth || validatingPlan) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 pt-20 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Si no está autenticado
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 pt-20 px-4">
        <div className="max-w-md mx-auto bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl p-8 text-center">
          <LogIn className="w-16 h-16 mx-auto text-blue-500 mb-4" />
          <h2 className="text-2xl font-bold mb-4">Inicia sesión para publicar</h2>
          <p className="text-gray-600 mb-6">
            Necesitas iniciar sesión para poder publicar una propiedad.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-xl 
                     font-medium transform transition-all duration-300
                     hover:from-blue-600 hover:to-purple-600 hover:scale-[1.02]
                     active:scale-98"
          >
            Ir a Iniciar Sesión
          </button>
        </div>
      </div>
    );
  }

  // Si hay error de plan o no puede publicar
  if (planError || (!validatingPlan && !canPublish)) {
    return (
      <PlanLimitMessage 
        planInfo={{
          typeId: user?.tipo_usuario_id,
          plan: user?.plan,
          totalProperties: planInfo?.totalProperties || 0,
          limit: planInfo?.limit || 0
        }}
        onUpgrade={() => navigate('/upgrade-plan')}
        onManage={() => navigate('/mis_propiedades')}
      />
    );
  }

  // Funciones auxiliares
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'largo_terreno' || name === 'ancho_terreno') {
      const newFormData = {
        ...formData,
        [name]: value
      };

      const largo = parseFloat(name === 'largo_terreno' ? value : formData.largo_terreno) || 0;
      const ancho = parseFloat(name === 'ancho_terreno' ? value : formData.ancho_terreno) || 0;
      const areaTotal = Math.round(largo * ancho);

      setFormData({
        ...newFormData,
        area_total: areaTotal.toString(),
        area_ocupada: formData.area_completa ? areaTotal.toString() : formData.area_ocupada
      });
    } else if (name === 'titulo') {
      const formattedValue = capitalizeFirstLetter(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;

    if (!isAutoConverting) {
      setIsAutoConverting(true);
      
      if (formData.operacion === 'comprar') {
        if (name === 'precio_soles') {
          const dolares = value ? Math.round(((parseFloat(value) / TIPO_CAMBIO) * 100) / 100).toFixed(0) : '';
          setFormData(prev => ({
            ...prev,
            precio_soles: value,
            precio_dolares: dolares
          }));
        } else if (name === 'precio_dolares') {
          const soles = value ? Math.round((parseFloat(value) * TIPO_CAMBIO)) : '';
          setFormData(prev => ({
            ...prev,
            precio_dolares: value,
            precio_soles: soles
          }));
        }
      } else {
        if (name === 'precio_mensual') {
          const dolares = value ? Math.round(((parseFloat(value) / TIPO_CAMBIO) * 100) / 100).toFixed(0) : '';
          setFormData(prev => ({
            ...prev,
            precio_mensual: value,
            precio_dolares: dolares
          }));
        } else if (name === 'precio_dolares') {
          const soles = value ? Math.round((parseFloat(value) * TIPO_CAMBIO)) : '';
          setFormData(prev => ({
            ...prev,
            precio_dolares: value,
            precio_mensual: soles
          }));
        }
      }
      setIsAutoConverting(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (formData.imagenes.length + files.length > MAX_IMAGES) {
      setError(`Máximo ${MAX_IMAGES} imágenes permitidas`);
      return;
    }

    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        setError(`${file.name} no es una imagen válida`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError(`${file.name} excede el tamaño máximo de 5MB`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setFormData(prev => ({
      ...prev,
      imagenes: [...prev.imagenes, ...validFiles]
    }));
    setError('');
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      imagenes: prev.imagenes.filter((_, i) => i !== index)
    }));
  };

  const validateStep = () => {
    if (publicationType === 'proyecto') {
      switch (step) {
        case 1: // Información del proyecto
          return (
            formData.nombre_proyecto && 
            formData.descripcion_proyecto && 
            formData.descripcion_proyecto.length >= 100 &&
            formData.tipo_proyecto && 
            formData.estado_proyecto &&
            formData.mes_entrega &&
            formData.anio_entrega &&
            formData.total_unidades &&
            parseInt(formData.total_unidades) > 0
          );
        case 2: // Ubicación
          return formData.ubicacion && formData.mapa_direccion;
        case 3: // Características y unidades
          // Verificar que al menos un tipo de unidad esté seleccionado y configurado
          const hasValidUnits = [1, 2, 3, 4].some(num => {
            const hasUnit = formData[`tiene_${num}dorm`];
            if (!hasUnit) return false;
            
            const units = formData[`unidades_${num}dorm`];
            const area = formData[`area_${num}dorm`];
            const price = formData[`precio_${num}dorm_soles`];
            
            return units && parseInt(units) > 0 && 
                   area && parseFloat(area) > 0 && 
                   price && parseFloat(price) > 0;
          });
          return hasValidUnits;
        case 4: // Lugares cercanos y servicios
          return formData.lugares_cercanos.length > 0 && formData.servicios_basicos.length > 0;
        case 5: // Imágenes
          return formData.imagenes.length >= MIN_IMAGES;
        default:
          return false;
      }
    }
    
    // Validación estándar para propiedades
    switch (step) {
      case 1:
        if (formData.operacion === 'comprar') {
          return (
            formData.tipo && 
            formData.titulo && 
            formData.precio_soles && 
            parseFloat(formData.precio_soles) > 0 &&
            formData.descripcion_corta && 
            formData.descripcion_corta.length >= 50 && 
            formData.descripcion_corta.length <= 500
          );
        } else {
          return (
            formData.tipo && 
            formData.titulo && 
            formData.precio_mensual && 
            parseFloat(formData.precio_mensual) > 0 &&
            formData.descripcion_corta && 
            formData.descripcion_corta.length >= 50 && 
            formData.descripcion_corta.length <= 500
          );
        }
      case 2:
        return formData.ubicacion && formData.mapa_direccion;
      case 3:
        const baseValidation = formData.area_total && parseFloat(formData.area_total) > 0 && 
                                formData.area_ocupada && parseFloat(formData.area_ocupada) > -1;
        if (formData.tipo === 'terreno') {
          return baseValidation;
        }
        return baseValidation && 
               formData.habitaciones && 
               parseInt(formData.habitaciones) >= 0 && 
               formData.banos && 
               parseInt(formData.banos) >= 0;
      case 4:
        return formData.lugares_cercanos.length > 0 && formData.servicios_basicos.length > 0;
      case 5:
        return formData.imagenes.length >= MIN_IMAGES;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      if (step < 5) {
        setStep(step + 1);
        setError('');
      }
    } else {
      if (publicationType === 'proyecto') {
        switch (step) {
          case 1:
            setError('Complete todos los campos requeridos del proyecto');
            break;
          case 3:
            setError('Configure al menos un tipo de unidad con cantidad, área y precio');
            break;
          default:
            setError('Por favor complete todos los campos requeridos');
        }
      } else {
        setError('Por favor complete todos los campos requeridos');
      }
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!validateStep()) {
      setError('Por favor complete todos los campos requeridos');
      return;
    }
  
    if (!token) {
      setError('No se encontró el token de autenticación. Por favor, inicie sesión nuevamente.');
      return;
    }
  
    if (formData.imagenes.length < MIN_IMAGES) {
      setError(`Debe subir al menos ${MIN_IMAGES} imagen(es)`);
      return;
    }
  
    if (formData.imagenes.length > MAX_IMAGES) {
      setError(`No puede subir más de ${MAX_IMAGES} imágenes`);
      return;
    }
    
    setLoading(true);
    setError('');
  
    try {
      const formDataToSend = new FormData();
  
      Object.keys(formData).forEach((key) => {
        if (key === 'lugares_cercanos' || key === 'servicios_basicos' || key === 'bancos_financiamiento' || key === 'amenidades') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } 
        else if (key !== 'imagenes' && !key.startsWith('_')) {
          if (formData.operacion === 'comprar') {
            if (['precio_mensual', 'mantenimiento'].includes(key)) return;
          } else {
            if (['precio_soles'].includes(key)) return;
          }
  
          if (key === 'estacionamiento') {
            formDataToSend.append(key, formData[key] ? 'Si' : 'No');
          } 
          else if (key === 'area_completa') {
            formDataToSend.append(key, formData[key] ? '1' : '0');
          }
          else if (key.startsWith('tiene_') && key.endsWith('dorm')) {
            formDataToSend.append(key, formData[key] ? 'true' : 'false');
          }
          else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });
  
      formData.imagenes.forEach((image) => {
        formDataToSend.append('imagenes', image);
      });
  
      const endpoint = publicationType === 'proyecto' 
        ? 'https://backend-production-1786.up.railway.app/api/proyectos' 
        : 'https://backend-production-1786.up.railway.app/api/propiedades';
  
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend,
      });
  
      const responseData = await response.json();
      
      if (!response.ok) {
        console.error('Full error response:', responseData);
        throw new Error(
          responseData.details || 
          responseData.error || 
          'Error al guardar la publicación'
        );
      }
  
      clearFormData();
  
      if (publicationType === 'proyecto') {
        navigate(`/proyecto/${responseData.propertyId}`);
      } else if (formData.operacion === 'alquiler') {
        navigate(`/alquiler/${responseData.propertyId}`);
      } else {
        navigate(`/propiedad/${responseData.propertyId}`);
      }
  
    } catch (error) {
      console.error('Submission Error:', error);
      setError(error.message || 'Error al procesar la solicitud');
      
      if (error instanceof TypeError) {
        console.error('Network Error:', error.message);
        setError('Error de red. Por favor, verifica tu conexión.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 pt-0 pb-10 px-4">
      <div className="max-w-4xl mx-auto bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl p-6">
        {/* Banner de datos recuperados */}
        {showRecoveredBanner && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-lg flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-green-800 flex items-center">
                <Check className="w-5 h-5 mr-2" />
                Datos recuperados
              </h3>
              <p className="text-green-700">
                Hemos recuperado tu información. Puedes continuar desde donde lo dejaste.
              </p>
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={() => {
                  clearFormData();
                  setFormData(initialFormData);
                  setStep(1);
                  setShowRecoveredBanner(false);
                }}
                className="text-red-600 hover:text-red-800 text-sm flex items-center"
              >
                <X className="w-4 h-4 mr-1" />
                Borrar datos
              </button>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="h-2 bg-gray-200 rounded-full">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all"
              style={{ width: `${(step / 5) * 100}%` }}
            />
          </div>
          <p className="text-center mt-2 text-gray-600">
            Paso {step} de 5 - {publicationType === 'proyecto' ? 'Proyecto Inmobiliario' : 'Propiedad'}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-xl">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {publicationType === 'proyecto' ? (
            // Flujo para proyectos inmobiliarios
            <>
              {step === 1 && (
                <InfProyPublish 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  errors={error ? { error } : {}}
                />
              )}
              
              {step === 2 && (
                <UbiPublish 
                  formData={formData}
                  setFormData={setFormData}
                  searchBoxRef={searchBoxRef}
                />
              )}
              
              {step === 3 && (
                <CarProyPublish 
                  formData={formData}
                  setFormData={setFormData}
                  handleInputChange={handleInputChange}
                  errors={error ? { error } : {}}
                />
              )}

              {step === 4 && (
                <LugPublish 
                  formData={formData}
                  setFormData={setFormData}
                />
              )}
              
              {step === 5 && (
                <ImgPublish 
                  formData={formData}
                  handleImageUpload={handleImageUpload}
                  removeImage={removeImage}
                  errors={error}
                />
              )}
            </>
          ) : (
            // Flujo para propiedades
            <>
              {step === 1 && (
                <InfPublish 
                  formData={formData} 
                  handleInputChange={handleInputChange}
                  handleCurrencyChange={handleCurrencyChange}
                  errors={error}
                  isAutoConverting={isAutoConverting}
                />
              )}
        
              {step === 2 && (
                <UbiPublish 
                  formData={formData}
                  setFormData={setFormData}
                  searchBoxRef={searchBoxRef}
                />
              )}
        
              {step === 3 && (
                <CarPublish 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  setFormData={setFormData}
                />
              )}
        
              {step === 4 && (
                <LugPublish 
                  formData={formData}
                  setFormData={setFormData}
                />
              )}
        
              {step === 5 && (
                <ImgPublish 
                  formData={formData}
                  handleImageUpload={handleImageUpload}
                  removeImage={removeImage}
                  errors={error}
                />
              )}
            </>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            {step > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Anterior
              </button>
            )}

            {step < 5 ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all font-medium ml-auto flex items-center gap-2"
              >
                Siguiente
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className={`px-6 py-3 text-white rounded-lg ml-auto transition-all font-medium flex items-center gap-2
                  ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'}`}
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Publicando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Publicar {publicationType === 'proyecto' ? 'Proyecto' : 'Propiedad'}
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Publish;